import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, ArrowRight, HeartHandshake, Star } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { getManagedHomeOverview, listManagedHomes, prepareRepeatHomeBooking } from "@/lib/homes.functions";

export const Route = createFileRoute("/_authenticated/home/partners")({
  validateSearch: (search: Record<string, unknown>) => ({ homeId: typeof search['homeId'] === "string" ? search['homeId'] : undefined }),
  head: () => ({
    meta: [
      { title: "Previous Partners — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PreviousPartnersPage,
});

function PreviousPartnersPage() {
  const { homeId: requestedHomeId } = Route.useSearch();
  const navigate = useNavigate();
  const homes = useQuery({
    queryKey: ["managed-homes"],
    queryFn: () => listManagedHomes(),
  });
  const homeId = homes.data?.find((home) => home.id === requestedHomeId)?.id ?? homes.data?.find((home) => home.isActive)?.id ?? homes.data?.find((home) => !home.isShared)?.id ?? homes.data?.[0]?.id ?? "";
  const overview = useQuery({
    queryKey: ["managed-home-overview", homeId],
    queryFn: () => getManagedHomeOverview({ data: { homeId } }),
    enabled: Boolean(homeId),
  });

  const partners = overview.data?.ok ? overview.data.previousPartners : [];
  const [bookingAgainId, setBookingAgainId] = useState<string | null>(null);

  async function bookAgain(bookingId: string) {
    if (bookingAgainId) return;
    setBookingAgainId(bookingId);
    try {
      const result = await prepareRepeatHomeBooking({ data: { bookingId } });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      toast.success("We’ll prioritise your previous Partner where they’re eligible.");
      await navigate({
        to: "/book",
        search: { ...(result.serviceSlug ? { service: result.serviceSlug } : {}), homeId },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not prepare this repeat booking");
    } finally {
      setBookingAgainId(null);
    }
  }

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-24">
      <AppHeader workspace="customer" />
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/home" className="inline-flex items-center gap-2 text-sm font-bold text-[#66758d]">
          <ArrowLeft className="h-4 w-4" /> My Home
        </Link>

        <header className="mt-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#c99522]">Trusted relationships</p>
          <h1 className="mt-1 text-3xl font-extrabold text-[#061b3a]">Previous Partners</h1>
          <p className="mt-2 max-w-xl text-sm leading-6 text-[#66758d]">
            Rebook professionals you’ve genuinely used through Shields. Your repeat preference is explicit and never inferred from a review.
          </p>
        </header>

        {!homeId ? (
          <section className="mt-6 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-7 text-center">
            <HeartHandshake className="mx-auto h-8 w-8 text-[#0b3568]" />
            <h2 className="mt-3 font-extrabold text-[#061b3a]">Add your home first</h2>
            <p className="mt-1 text-sm text-[#66758d]">Previous Partners are kept with the property and service history they belong to.</p>
            <Link to="/home" className="mt-5 inline-flex min-h-11 items-center rounded-xl bg-[#e1b64f] px-4 text-sm font-extrabold text-[#061b3a]">
              Manage My Home
            </Link>
          </section>
        ) : overview.isLoading ? (
          <div className="mt-6 rounded-2xl border border-[#e1e8f0] bg-white p-5 text-sm text-[#66758d]">Loading previous Partners…</div>
        ) : overview.isError || (overview.data && !overview.data.ok) ? (
          <section className="mt-6 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-7 text-center">
            <HeartHandshake className="mx-auto h-8 w-8 text-destructive" />
            <h2 className="mt-3 font-extrabold text-[#061b3a]">We couldn’t load your previous Partners</h2>
            <p className="mt-1 text-sm text-[#66758d]">
              {overview.data && !overview.data.ok ? overview.data.error : "Try again. Your service history has not been changed."}
            </p>
            <button type="button" onClick={() => overview.refetch()} className="btn-secondary-hero mt-5">Try again</button>
          </section>
        ) : partners.length ? (
          <section className="mt-6 space-y-3">
            {partners.map((partner: any) => (
              <article key={partner.partnerId} className="rounded-2xl border border-[#d8e6f4] bg-[linear-gradient(145deg,#ffffff,#f7fbff)] p-4 shadow-[0_8px_24px_rgba(6,27,58,.04)]">
                <div className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#eef4fb] text-[#0b3568]">
                    <HeartHandshake className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="block text-base text-[#061b3a]">{partner.tradingName}</strong>
                    <small className="mt-0.5 block text-sm text-[#66758d]">{partner.serviceName}</small>
                    {partner.rating != null && (
                      <span className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#9a6b16]">
                        <Star className="h-3.5 w-3.5 fill-current" />
                        {Number(partner.rating).toFixed(1)} ({partner.ratingCount})
                      </span>
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => bookAgain(partner.lastBookingId)}
                  disabled={Boolean(bookingAgainId)}
                  className="mt-4 flex min-h-11 w-full items-center justify-center rounded-xl bg-[#e1b64f] px-4 text-sm font-extrabold text-[#061b3a] hover:bg-[#d8a82f] disabled:cursor-wait disabled:opacity-60"
                >
                  {bookingAgainId === partner.lastBookingId ? "Preparing…" : "Book Again"} <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </article>
            ))}
          </section>
        ) : (
          <section className="mt-6 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-7 text-center">
            <HeartHandshake className="mx-auto h-8 w-8 text-[#0b3568]" />
            <h2 className="mt-3 font-extrabold text-[#061b3a]">No previous Partners yet</h2>
            <p className="mt-1 text-sm text-[#66758d]">Completed Shields jobs will build this list automatically.</p>
            <Link to="/book" className="mt-5 inline-flex min-h-11 items-center rounded-xl bg-[#e1b64f] px-4 text-sm font-extrabold text-[#061b3a]">
              Book a Service
            </Link>
          </section>
        )}
      </main>
    </div>
  );
}
